import React from "react";
//#region types
import { CostForwarding, CostForwardingSource } from "models/utilTypes/costForwardingTypes";
//#endregion
//#region components
import { useCostForwardingService } from "serviceHooks";
//#endregion

export interface CostForwardingMarkingResult {
  marking?: CostForwarding;
  /** False until the read succeeds, and stays false when it fails. */
  isAvailable: boolean;
  reload: () => void;
}

interface LoadedMarking {
  sourceId: number;
  marking?: CostForwarding;
  isAvailable: boolean;
}

/**
 * Loads the marking of one document. `reloadToken` refetches when it changes, so passing the document
 * object picks up a marking the footer wrote while the document was being approved.
 */
function useCostForwardingMarking(
  source: CostForwardingSource,
  sourceId: number | undefined,
  reloadToken?: unknown
): CostForwardingMarkingResult {
  const { getMarking } = useCostForwardingService();
  const [loaded, setLoaded] = React.useState<LoadedMarking>();
  const [reloadCount, setReloadCount] = React.useState(0);

  React.useEffect(() => {
    if (!sourceId) return;

    // Guards against a slower response for a previous document landing after the current one's.
    let isCurrent = true;
    getMarking(source, sourceId)
      .then((marking) => isCurrent && setLoaded({ sourceId, marking, isAvailable: true }))
      .catch(() => isCurrent && setLoaded({ sourceId, isAvailable: false }));

    return () => {
      isCurrent = false;
    };
  }, [source, sourceId, reloadToken, reloadCount]);

  const reload = React.useCallback(() => setReloadCount((count) => count + 1), []);

  const isForThisDocument = loaded !== undefined && loaded.sourceId === sourceId;
  const marking = isForThisDocument ? loaded.marking : undefined;
  const isAvailable = isForThisDocument ? loaded.isAvailable : false;

  return React.useMemo(() => ({ marking, isAvailable, reload }), [marking, isAvailable, reload]);
}

export default useCostForwardingMarking;
